import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, Clock, Ban, MailQuestion, LogIn, Mail } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { claimShare } from "@/features/sharing/sharing";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/share/$token")({
  head: () => ({
    meta: [
      { title: "Open shared item — AskBell" },
      { name: "description", content: "Claim shared access to a chat or project." },
    ],
  }),
  component: SharePage,
});

type ErrCode =
  | "wrong_email"
  | "revoked"
  | "expired"
  | "not_found"
  | "not_authenticated"
  | "claim_failed";

type ErrorView = {
  Icon: typeof ShieldAlert;
  iconClass: string;
  title: string;
  description: string;
  showRequestAccess: boolean;
  showSignInAs: boolean;
};

function errorView(code: ErrCode, currentEmail?: string | null): ErrorView {
  switch (code) {
    case "wrong_email":
      return {
        Icon: MailQuestion,
        iconClass: "text-ui-warning-icon",
        title: "Wrong account",
        description: currentEmail
          ? `You're signed in as ${currentEmail}, but this link was sent to a different AskBell account. Sign in with the account the owner shared it with.`
          : "This link was sent to a different AskBell account. Sign in with the account the owner shared it with.",
        showRequestAccess: false,
        showSignInAs: true,
      };
    case "expired":
      return {
        Icon: Clock,
        iconClass: "text-ui-warning-icon",
        title: "Link expired",
        description: "This share link is no longer valid because it expired. Ask the owner to send you a fresh link.",
        showRequestAccess: true,
        showSignInAs: false,
      };
    case "revoked":
      return {
        Icon: Ban,
        iconClass: "text-destructive",
        title: "Access revoked",
        description: "The owner revoked access to this item. If you still need access, request a new link from them.",
        showRequestAccess: true,
        showSignInAs: false,
      };
    case "not_found":
      return {
        Icon: ShieldAlert,
        iconClass: "text-muted-foreground",
        title: "Link not found",
        description: "We couldn't find this share. Double-check the URL, or ask the owner to share it again.",
        showRequestAccess: true,
        showSignInAs: false,
      };
    case "not_authenticated":
      return {
        Icon: LogIn,
        iconClass: "text-ui-warning-icon",
        title: "Sign in required",
        description: "You need to sign in with your Bellomy account to open this shared item.",
        showRequestAccess: false,
        showSignInAs: false,
      };
    default:
      return {
        Icon: ShieldAlert,
        iconClass: "text-destructive",
        title: "Can't open this link",
        description: "Something went wrong while opening this share. Please try again, or ask the owner to resend it.",
        showRequestAccess: true,
        showSignInAs: false,
      };
  }
}

function SharePage() {
  const { token } = Route.useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<ErrCode | null>(null);
  const [working, setWorking] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Bounce to login, then come back here
      const next = `/share/${token}`;
      window.location.href = `/login?next=${encodeURIComponent(next)}`;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await claimShare(token);
        if (cancelled) return;
        if (res.resource_type === "chat") {
          navigate({ to: "/chat", search: { conversation: res.resource_id }, replace: true });
        } else {
          navigate({
            to: "/projects/$projectId",
            params: { projectId: res.resource_id },
            replace: true,
          });
        }
      } catch (e: any) {
        if (cancelled) return;
        const code = (e?.message ?? "claim_failed") as ErrCode;
        setError(code);
        setWorking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user, token, navigate]);

  if (working) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-ui-base">Opening shared item…</p>
        </div>
      </div>
    );
  }

  const view = errorView(error ?? "claim_failed", user?.email);
  const { Icon } = view;

  const requestAccessHref =
    `mailto:?subject=${encodeURIComponent("Request access to a shared AskBell item")}` +
    `&body=${encodeURIComponent(
      `Hi,\n\nI tried to open a shared AskBell link but it's no longer accessible.\n\nLink: ${typeof window !== "undefined" ? window.location.href : ""}\n\nCould you re-share it with me?\n\nThanks!`,
    )}`;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md w-full rounded-ui-md border border-border bg-card p-6 text-center">
        <Icon className={`h-10 w-10 ${view.iconClass} mx-auto mb-3`} />
        <h1 className="text-ui-xl font-bold text-foreground">{view.title}</h1>
        <p className="mt-2 text-ui-base text-muted-foreground whitespace-pre-line">
          {view.description}
        </p>
        <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
          {view.showRequestAccess && (
            <Button asChild variant="default">
              <a href={requestAccessHref}>
                <Mail className="h-4 w-4" />
                Request access
              </a>
            </Button>
          )}
          {view.showSignInAs && (
            <Button
              variant="default"
              onClick={() => {
                const next = `/share/${token}`;
                window.location.href = `/login?next=${encodeURIComponent(next)}&switch=1`;
              }}
            >
              <LogIn className="h-4 w-4" />
              Sign in with another account
            </Button>
          )}
          <Button asChild variant="outline">
            <Link to="/chat">Go to AskBell</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
